// forceLayout.ts — d3-force layout for the graph explorer.
//
// React Flow ships no layout algorithm, so we run a d3-force simulation to a
// stable state and read the resulting positions. The notable piece is the
// rectangular-collision force (nodes are DOM cards, not points), adapted from
// the official React Flow force-layout example: a quadtree sweep that pushes
// overlapping node rectangles apart instead of treating them as circles.

import {
    forceSimulation,
    forceManyBody,
    forceLink,
    forceX,
    forceY,
    type SimulationNodeDatum,
    type SimulationLinkDatum,
} from 'd3-force'

import type { GraphEdge } from './adapter'

// Fixed node card footprint used for collision (kept in sync with AssetNode's
// min sizing). Slightly larger than the rendered card to leave breathing room.
export const NODE_WIDTH = 200
export const NODE_HEIGHT = 120

export interface SimNode extends SimulationNodeDatum {
    id: string
    // fixed position for the focal node (fx/fy come from SimulationNodeDatum).
}

// rectCollide is a rectangular-collision force (nodes are DOM cards, not
// points): for every overlapping pair it nudges them apart along the axis of
// least penetration. A plain pairwise sweep — O(n²) per tick — is cheap at the
// ~200-node soft cap the explorer enforces, and avoids pulling in d3-quadtree.
export function rectCollide(width: number, height: number) {
    let nodes: SimNode[] = []
    const padding = 16
    const minGapX = width + padding
    const minGapY = height + padding

    function force() {
        for (let i = 0; i < nodes.length; i++) {
            const a = nodes[i]
            const ax = a.x ?? 0
            const ay = a.y ?? 0
            for (let j = i + 1; j < nodes.length; j++) {
                const b = nodes[j]
                const bx = b.x ?? 0
                const by = b.y ?? 0
                const dx = ax - bx
                const dy = ay - by
                const overlapX = minGapX - Math.abs(dx)
                const overlapY = minGapY - Math.abs(dy)
                if (overlapX > 0 && overlapY > 0) {
                    if (overlapX < overlapY) {
                        const shift = ((dx === 0 ? 1 : Math.sign(dx)) * overlapX) / 2
                        a.x = (a.x ?? 0) + shift
                        b.x = (b.x ?? 0) - shift
                    } else {
                        const shift = ((dy === 0 ? 1 : Math.sign(dy)) * overlapY) / 2
                        a.y = (a.y ?? 0) + shift
                        b.y = (b.y ?? 0) - shift
                    }
                }
            }
        }
    }

    force.initialize = (n: SimNode[]) => {
        nodes = n
    }
    return force
}

export interface LayoutInput {
    nodeIds: string[]
    edges: GraphEdge[]
    focusId?: string
    // Previous positions to seed from, so incremental expansions don't reshuffle
    // the whole canvas.
    previous?: Record<string, { x: number; y: number }>
    width?: number
    height?: number
}

// computeLayout runs the simulation to a stable state and returns a map of
// nodeId → {x, y}. Deterministic given the same input (fixed seed positions and
// a fixed tick count), so URL-replayed graphs land in the same shape.
export function computeLayout(input: LayoutInput): Record<string, { x: number; y: number }> {
    const { nodeIds, edges, focusId, previous = {}, width = 1200, height = 800 } = input
    const cx = width / 2
    const cy = height / 2

    const simNodes: SimNode[] = nodeIds.map((id, i) => {
        const prev = previous[id]
        if (prev) return { id, x: prev.x, y: prev.y }
        // Seed new nodes deterministically on a ring around the center so the
        // layout is reproducible (no Math.random()).
        const angle = (i / Math.max(1, nodeIds.length)) * Math.PI * 2
        return { id, x: cx + Math.cos(angle) * 240, y: cy + Math.sin(angle) * 240 }
    })

    const nodeById = new Map(simNodes.map((n) => [n.id, n]))
    // Pin the focal node at the center.
    if (focusId && nodeById.has(focusId)) {
        const f = nodeById.get(focusId)!
        f.fx = cx
        f.fy = cy
    }

    const simLinks: SimulationLinkDatum<SimNode>[] = edges
        .filter((e) => nodeById.has(e.source) && nodeById.has(e.target))
        .map((e) => ({ source: e.source, target: e.target }))

    const sim = forceSimulation<SimNode>(simNodes)
        .force(
            'link',
            forceLink<SimNode, SimulationLinkDatum<SimNode>>(simLinks)
                .id((d) => d.id)
                .distance(220)
                .strength(0.4),
        )
        .force('charge', forceManyBody().strength(-800))
        .force('x', forceX(cx).strength(0.05))
        .force('y', forceY(cy).strength(0.05))
        .force('collide', rectCollide(NODE_WIDTH, NODE_HEIGHT))
        .stop()

    // Run synchronously to convergence (fixed tick budget keeps it deterministic).
    const ticks = 300
    for (let i = 0; i < ticks; i++) sim.tick()

    const out: Record<string, { x: number; y: number }> = {}
    for (const n of simNodes) {
        out[n.id] = { x: n.x ?? cx, y: n.y ?? cy }
    }
    return out
}
